import { RuleRow } from '@innovatm/js-rule-editor';
import fileDownload from 'js-file-download';
import { useConfirm } from 'material-ui-confirm';
import { memo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { KeyGetGetRules, Rule, useMutationDeleteDeleteRule, useMutationGetGetRuleFile } from '../../backend';
import { useCurrentUserClient } from '../../contexts/OidcContext/OidcContext.selectors';
import { ROUTE_EDITOR } from '../../routes';
import { errorToastConfig, successToastConfig } from '../../utils/utils';

export const RuleRowDisplay = memo(function RuleRowDisplay({ rule }: { rule: Rule }) {
    const navigate = useNavigate();
    const client = useCurrentUserClient();
    const queryClient = useQueryClient();
    const confirm = useConfirm();

    const { mutateAsync: deleteRule } = useMutationDeleteDeleteRule({
        onSuccess: () => {
            navigate(ROUTE_EDITOR);
            queryClient.invalidateQueries(KeyGetGetRules);
            toast(`The rule is successfully deleted.`, successToastConfig);
        },
        onError: error => {
            Object.keys(error?.response?.data).map(errorKey =>
                toast(`${error?.response?.data[errorKey]}`, errorToastConfig),
            );
        },
    });

    const { mutateAsync: downloadFile } = useMutationGetGetRuleFile({
        onError: error => {
            Object.keys(error?.response?.data).map(errorKey =>
                toast(`${error?.response?.data[errorKey]}`, errorToastConfig),
            );
        },
    });

    const onDelete = useCallback(() => {
        confirm({
            content: `Are you sure you want to delete rule ${rule.name} ?`,
        }).then(() => deleteRule({ name: rule.name!, context: client.airportIata }));
    }, [client.airportIata, confirm, deleteRule, rule.name]);

    const onDownload = useCallback(() => {
        downloadFile({
            context: client.airportIata,
            name: rule.name,
        }).then(response => fileDownload(response.data, `${client.airportIata}_${rule.name}.drl`));
    }, [client.airportIata, downloadFile, rule.name]);

    return (
        <RuleRow
            rule={rule}
            onDelete={onDelete}
            onDuplicate={() => navigate(`${ROUTE_EDITOR}/${rule.type}/${rule.name}/duplicate`)}
            onUpdate={() => navigate(`${ROUTE_EDITOR}/${rule.type}/${rule.name}`)}
            onDownload={onDownload}
        />
    );
});

export const renderRow = (rule: Rule) => <RuleRowDisplay key={rule.name} rule={rule} />;
